import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Menu, X, Search, User, Heart, ShoppingCart, Phone, Settings } from 'lucide-react';
import { Button } from './ui/button';
import AnnouncementBar from './AnnouncementBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Header = ({ cartCount = 0, wishlistCount = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [navigation, setNavigation] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location]);

  useEffect(() => {
    fetchNavigation();
    fetchSiteSettings();
  }, []);

  const fetchNavigation = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cms/navigation`);
      const items = response.data.items || [];
      setNavigation(items.filter(item => item.is_active).sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Failed to fetch navigation:', error);
      setNavigation([
        { id: '1', label: 'Home', path: '/', order: 1 },
        { id: '2', label: 'Shop', path: '/shop', order: 2 },
        { id: '3', label: 'About Us', path: '/about', order: 5 },
        { id: '4', label: 'Contact', path: '/contact', order: 6 },
      ]);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cms/site-settings`);
      setSiteSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  return (
    <>
      <div className="sticky top-0 z-50">
        <AnnouncementBar />
        
        <header
          className={`transition-all duration-300 ${
            isScrolled 
              ? 'bg-white/95 backdrop-blur-md shadow-lg' 
              : 'bg-white'
          } border-b border-gold/20`}
          data-testid="site-header"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 md:h-20">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setMobileMenuOpen(true)}
                  className="lg:hidden p-2 hover:bg-cream"
                  data-testid="mobile-menu-button"
                >
                  <Menu className="w-6 h-6 text-maroon" />
                </Button>
                
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2"
                  data-testid="logo-button"
                >
                  {siteSettings?.logo_url ? (
                    <img src={siteSettings.logo_url} alt={siteSettings?.site_name || 'Indicraft'} className="h-10" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-maroon to-maroon-dark flex items-center justify-center">
                        <span className="text-gold font-accent text-lg">ॐ</span>
                      </div>
                      <div className="flex flex-col">
                        <h1 className="text-xl md:text-2xl font-heading font-bold text-maroon tracking-tight">
                          {siteSettings?.site_name || 'Indicraft'}
                        </h1>
                        <span className="text-[10px] text-gold font-accent tracking-widest hidden sm:block">
                          {siteSettings?.tagline || 'SACRED PUJA ESSENTIALS'}
                        </span>
                      </div>
                    </div>
                  )}
                </button>
              </div>

              <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
                {navigation.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.path)}
                    data-testid={`nav-link-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                      isActive(item.path)
                        ? 'text-white bg-maroon'
                        : 'text-maroon hover:text-maroon-dark hover:bg-cream'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setSearchOpen(!searchOpen)}
                  className="p-2 hover:bg-cream rounded-full"
                  data-testid="search-button"
                >
                  <Search className="w-5 h-5 text-maroon" />
                </Button>
                
                <Button
                  variant="ghost"
                  onClick={() => navigate('/account')}
                  className="p-2 hover:bg-cream rounded-full hidden sm:flex"
                  data-testid="account-button"
                >
                  <User className="w-5 h-5 text-maroon" />
                </Button>
                
                <Button
                  variant="ghost"
                  onClick={() => navigate('/wishlist')}
                  className="p-2 hover:bg-cream rounded-full relative"
                  data-testid="wishlist-button"
                >
                  <Heart className="w-5 h-5 text-maroon" />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-saffron text-white text-xs rounded-full flex items-center justify-center">
                      {wishlistCount}
                    </span>
                  )}
                </Button>
                
                <Button
                  variant="ghost"
                  onClick={() => navigate('/cart')}
                  className="p-2 hover:bg-cream rounded-full relative"
                  data-testid="cart-button"
                >
                  <ShoppingCart className="w-5 h-5 text-maroon" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-saffron text-white text-xs rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {searchOpen && (
              <div className="pb-4 animate-fade-in-up" data-testid="search-bar">
                <form onSubmit={handleSearch} className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for puja items, diyas, idols..."
                    className="w-full h-12 pl-12 pr-4 rounded-full border-2 border-gold/30 focus:border-gold focus:outline-none bg-cream"
                    autoFocus
                  />
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-maroon/60" />
                  <Button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-maroon hover:bg-maroon-dark text-white rounded-full px-6"
                  >
                    Search
                  </Button>
                </form>
              </div>
            )}
          </div>
        </header>
      </div>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}

      <div
        className={`fixed top-0 left-0 bottom-0 w-[85%] max-w-sm bg-cream z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-menu-drawer"
      >
        <div className="flex items-center justify-between p-4 border-b border-gold/20 bg-maroon">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
              <span className="text-gold font-accent">ॐ</span>
            </div>
            <h2 className="text-xl font-heading font-bold text-white">
              {siteSettings?.site_name || 'Indicraft'}
            </h2>
          </div>
          <Button
            variant="ghost"
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 text-white hover:bg-white/10"
            data-testid="mobile-menu-close"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>

        <nav className="p-4 overflow-y-auto h-[calc(100%-80px)]">
          <div className="space-y-2 mb-6">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive(item.path)
                    ? 'text-white bg-maroon'
                    : 'text-maroon hover:bg-gold/10'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="border-t border-gold/20 pt-4">
            <button
              onClick={() => navigate('/track-order')}
              className="w-full text-left px-4 py-3 rounded-lg font-medium text-maroon hover:bg-gold/10"
            >
              Track Order
            </button>
            <button
              onClick={() => navigate('/faq')}
              className="w-full text-left px-4 py-3 rounded-lg font-medium text-maroon hover:bg-gold/10"
            >
              FAQ
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="w-full text-left px-4 py-3 rounded-lg font-medium text-gold bg-maroon hover:bg-maroon-dark flex items-center gap-2 mt-4"
              data-testid="mobile-admin-btn"
            >
              <Settings className="w-4 h-4" />
              Admin Panel
            </button>
          </div>

          {siteSettings?.contact_phone && (
            <div className="mt-6 p-4 bg-white rounded-xl border border-gold/20">
              <p className="text-sm text-maroon/60 mb-2">Need Help?</p>
              <a 
                href={`tel:${siteSettings.contact_phone}`}
                className="flex items-center gap-2 text-maroon font-semibold"
              >
                <Phone className="w-4 h-4" />
                {siteSettings.contact_phone}
              </a>
            </div>
          )}
        </nav>
      </div>
    </>
  );
};

export default Header;
